import {
  ErrorSchemaBuilder,
  type ErrorSchema,
  type FieldProps,
} from '@rjsf/utils'
import { DefinitiveQuantityField } from 'components/DefinitiveInputs/DefinitiveQuantityField'
import { DefinitiveInputValidators } from 'components/DefinitiveInputs/Validators'
import { isEmpty, isEqual } from 'lodash'
import { useDefaultQuantity } from 'model/Quantity'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { QuantityDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { dashify } from 'utils/dashify'

function toErrorSchema(
  errors?: string[],
): ErrorSchema<QuantityDto> | undefined {
  const errorSchemaBuilder = new ErrorSchemaBuilder<QuantityDto>()

  if (errors && errors.length) {
    errorSchemaBuilder.addErrors(errors, 'values')
  }
  return errors && errors.length ? errorSchemaBuilder.ErrorSchema : undefined
}

export function RJSFQuantityField(props: FieldProps<QuantityDto>) {
  const { formData, onChange, disabled, readonly, uiSchema } = props
  const { t } = useTranslation()

  const defaultQuantity = useDefaultQuantity(
    props.uiSchema.quantityDtoType || props.uiSchema.unitType,
    null,
    true,
  )

  // Track last payload we sent to prevent loops
  const lastDataRef = useRef<any>(formData)
  const lastErrRef = useRef<ErrorSchema<QuantityDto> | undefined>(undefined)

  // Ignore the very first inner onChange (from child's useEffect on mount)
  const mountedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleQuantityChange = useCallback(
    (value: QuantityDto, _isValid?: boolean, validations?: string[]) => {
      const err = toErrorSchema(validations)

      // If this is the initial mount echo, ignore once.
      if (!mountedRef.current) {
        lastDataRef.current = value
        lastErrRef.current = err
        return
      }

      // Avoid re-sending identical data/errors (prevents array churn)
      const sameData = isEqual(value, lastDataRef.current)
      const sameErr = isEqual(err, lastErrRef.current)

      if (sameData && sameErr) return

      // When data hasn't actually changed, reuse the original reference so RJSF
      // doesn't think the array item mutated.
      const dataToSend = isEqual(value, formData) ? formData : value

      // Call the field onChange with errorSchema; omit path/id for max compatibility
      onChange(dataToSend, err as any)

      lastDataRef.current = dataToSend
      lastErrRef.current = err
    },
    [formData, onChange],
  )

  // Pull any ui:options you want to forward (optional)
  const uiOpts = (uiSchema as any)?.['ui:options'] ?? {}

  // Safely resolve the label
  const label = useMemo(() => {
    const rawLabel = props.uiSchema['ui:label'] || props.uiSchema['ui:title']
    const label = rawLabel
      ? t(`ws-form-${dashify(rawLabel.toString())}`, {
          defaultValue: rawLabel.toString(),
          ns: 'project',
        })
      : undefined
    return label
  }, [props.uiSchema, t])

  return (
    <>
      <DefinitiveQuantityField
        quantityDto={
          isEmpty(formData) || !formData.unit
            ? defaultQuantity
            : (formData as QuantityDto)
        }
        onChange={handleQuantityChange}
        onChangeIfInvalid={true}
        disableWarningsIcon
        validation={(quantity) => {
          if (!props.required && quantity.value == null) return undefined

          return DefinitiveInputValidators.CombineValidators([
            props.uiSchema['min-value']
              ? DefinitiveInputValidators.MinValue(
                  quantity?.value,
                  props.uiSchema['min-value'],
                )
              : undefined,
            props.uiSchema['max-value']
              ? DefinitiveInputValidators.MaxValue(
                  quantity?.value,
                  props.uiSchema['max-value'],
                )
              : undefined,
          ])
        }}
        disabledUnit={uiOpts.disabledUnit ?? readonly ?? disabled}
        disabledValue={uiOpts.disabledValue ?? readonly ?? disabled}
        typographyVariant={uiOpts.typographyVariant ?? 'body2'}
        maxValue={uiOpts.maxValue}
        showThousandsSeparator={uiOpts.showThousandsSeparator}
        numericFormatProps={uiOpts.numericFormat}
        textFieldProps={
          uiOpts.textFieldProps || {
            size: 'small',
            disabled: readonly || disabled,
            fullWidth: true,
            label: label,
            required: props.required,
          }
        }
        showRawValueInTooltip={uiOpts.showRawValueInTooltip}
      />
    </>
  )
}
